import { motion } from "framer-motion";
import { Coins, ExternalLink, Flame, HeartHandshake } from "lucide-react";
import usePoll, { fmtNum, fmtUsd, timeAgo } from "../hooks/usePoll";
import { EASE } from "./Reveal";

const sol = (v, d = 3) => (v == null ? "—" : `${v.toFixed(d)} SOL`);

function Stat({ id, label, usd, solv, sub, tone = "text-white" }) {
    return (
        <div data-testid={`rewards-${id}`} className="flex flex-col gap-1">
            <span className="kicker !text-[10px]">{label}</span>
            <span data-testid={`rewards-${id}-usd`} className={`headline text-3xl leading-none md:text-4xl ${tone}`}>
                {fmtUsd(usd)}
            </span>
            <span className="font-mono text-[11px] text-slate-400">{sol(solv)}</span>
            {sub ? <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{sub}</span> : null}
        </div>
    );
}

export default function RewardsTracker() {
    const { data, loading } = usePoll("/rewards", 120000);
    const r = data && !data.error ? data : null;
    const pct = r && r.buybackBudgetSol > 0 ? (r.buybackSpentSol / r.buybackBudgetSol) * 100 : 0;

    return (
        <div data-testid="rewards-tracker" className="glass relative mt-20 overflow-hidden rounded-3xl border-cyan-400/20 p-6 md:p-10">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_70%_at_100%_0%,rgba(0,240,255,0.10),transparent)]" />
            <div className="relative">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-slate-300">
                        <span className={`h-2 w-2 rounded-full ${r ? "bg-green-400 animate-pulse-dot" : "bg-slate-500"}`} />
                        LIVE CREATOR REWARDS TRACKER
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <span
                            data-testid="rewards-sol-price"
                            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-300"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse-dot" />
                            SOL {r?.solUsd ? fmtUsd(r.solUsd) : "—"} · LIVE
                        </span>
                        <span data-testid="rewards-status" className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                            {loading ? "SCANNING SOLANA…" : r?.updatedAt ? `SYNCED ${timeAgo(r.updatedAt).toUpperCase()}` : "UNAVAILABLE"}
                        </span>
                        <a
                            href={r?.pumpfunRewardsUrl || "https://pump.fun/profile/HdmQS1vKbeWFLLwaM3pzWMQE7H8NvawrNSSntbcGMf7g?tab=creator-rewards"}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="rewards-pumpfun-link"
                            className="flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-widest text-cyan-300 hover:text-cyan-100"
                        >
                            PUMP.FUN REWARDS <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                    </div>
                </div>

                <div className="mt-8 grid gap-8 md:grid-cols-[1.2fr_1fr_1fr]">
                    <div className="flex flex-col gap-1 border-b border-white/10 pb-6 md:border-b-0 md:border-r md:pb-0 md:pr-8">
                        <span className="kicker !text-[10px]">TOTAL CREATOR REWARDS EARNED</span>
                        <span data-testid="rewards-total-usd" className="headline text-5xl leading-none text-white md:text-6xl">
                            {fmtUsd(r?.totalUsdAtPayout ?? r?.totalUsd)}
                        </span>
                        <span className="font-mono text-sm text-slate-400">
                            {sol(r?.totalSol)}
                            {r?.totalUsd != null && r?.totalUsdAtPayout != null ? (
                                <span data-testid="rewards-total-live-usd" className="text-slate-500"> · worth {fmtUsd(r.totalUsd)} at today's SOL price</span>
                            ) : null}
                        </span>
                        <span className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                            {r ? `${r.distributionCount} PAYOUTS FROM PUMP.FUN · USD AT SOL PRICE WHEN PAID (AS ON PUMP.FUN)` : "FROM PUMP.FUN PAYOUTS ON-CHAIN"}
                        </span>
                    </div>
                    <Stat
                        id="charity"
                        label={<span className="flex items-center gap-1.5"><HeartHandshake className="h-3.5 w-3.5 text-cyan-300" /> 5% → CASCADIA PIGEON RESCUE</span>}
                        usd={r?.charityUsd}
                        solv={r?.charitySol}
                        sub="PAID AUTOMATICALLY EVERY PAYOUT"
                        tone="text-cyan-300"
                    />
                    <Stat
                        id="buyback"
                        label={<span className="flex items-center gap-1.5"><Flame className="h-3.5 w-3.5 text-orange-400" /> 25% → BUYBACK & BURN</span>}
                        usd={r?.buybackSpentUsd}
                        solv={r?.buybackSpentSol}
                        sub={r ? `TARGET ${fmtUsd(r.buybackBudgetUsd)} · ${r.buybackCount} BUYBACKS` : "SPENT FROM THE DEV WALLET"}
                        tone="text-orange-400"
                    />
                </div>

                <div className="mt-8">
                    <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        <span className="flex items-center gap-1.5"><Coins className="h-3.5 w-3.5 text-orange-400" /> BUYBACK PROMISE: $25 OF EVERY $100 EARNED</span>
                        <span data-testid="rewards-buyback-pct" className={pct >= 100 ? "text-green-400" : "text-orange-300"}>
                            {r ? `${pct.toFixed(0)}% OF TARGET ${pct >= 100 ? "· PROMISE KEPT ✓" : ""}` : "—"}
                        </span>
                    </div>
                    <div className="relative mt-2 h-3 w-full overflow-hidden rounded-full border border-orange-500/20 bg-black/40">
                        <motion.div
                            data-testid="rewards-buyback-bar"
                            className={`h-full rounded-full ${pct >= 100 ? "bg-gradient-to-r from-orange-500 via-yellow-300 to-green-400" : "bg-gradient-to-r from-orange-700 via-orange-400 to-orange-200"} shadow-[0_0_20px_rgba(255,106,0,0.5)]`}
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(100, pct)}%` }}
                            transition={{ duration: 1.4, ease: EASE }}
                        />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                        <span data-testid="rewards-buyback-tokens">{r ? `${fmtNum(r.buybackTokens)} $BALD BOUGHT BACK` : ""}</span>
                        <span data-testid="rewards-burned-tokens">{r ? `${fmtNum(r.burnedTokens)} $BALD BURNED` : ""}</span>
                    </div>
                </div>

                {r?.distributions?.length ? (
                    <div className="mt-8">
                        <div className="kicker !text-[10px]">LATEST PAYOUTS</div>
                        <ul data-testid="rewards-payouts" className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                            {r.distributions.slice(0, 5).map((d) => (
                                <li key={d.signature}>
                                    <a
                                        href={d.solscan}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`rewards-payout-${d.signature.slice(0, 8)}`}
                                        className="flex flex-col gap-1 rounded-xl border border-white/5 bg-black/30 px-3 py-2.5 transition-colors hover:border-cyan-400/40"
                                    >
                                        <span className="font-mono text-xs font-bold text-white">{sol(d.totalSol, 4)}</span>
                                        <span className="font-mono text-[10px] text-cyan-300">→ {sol(d.charitySol, 5)} TO RESCUE</span>
                                        <span className="font-mono text-[10px] text-slate-500">
                                            {new Date(d.blockTime * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · SOLSCAN
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                    Every number is parsed from the dev wallet's on-chain transactions: Pump.fun payouts (95/5 split), buy swaps into the $BALD pool and SPL burns. Total earned uses the SOL price at the moment of each payout (same method as Pump.fun); donated and buyback figures use the live SOL price of the day.
                </p>
            </div>
        </div>
    );
}
